import React from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import { API } from "@/lib/api";

export const ResetPasswordPage = () => {
  const [step, setStep] = React.useState("request"); // "request" | "reset" | "done"
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [err, setErr] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);

  // Check if there's a token in the URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      setStep("reset");
    }
  }, []);

  const requestReset = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setMsg(res.data.message || "E-Mail wurde gesendet.");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    if (password !== confirmPassword) {
      setErr("Passwörter stimmen nicht überein");
      setLoading(false);
      return;
    }
    if (password.length < 8) {
      setErr("Passwort muss mind. 8 Zeichen haben");
      setLoading(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setMsg(res.data.message || "Passwort erfolgreich geändert!");
      setStep("done");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Fehler beim Zurücksetzen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--brand-bg, #F4F5F6)" }}>
      <Card className="w-full max-w-sm p-6 shadow-lg border-[var(--brand-border)]">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg" style={{ background: "var(--brand-primary, #2C3338)" }}>
            A
          </div>
          <div>
            <div className="font-[Playfair_Display] text-xl font-bold tracking-wide text-[var(--brand-ink)]">
              Passwort zurücksetzen
            </div>
          </div>
        </div>

        {step === "request" && (
          <form onSubmit={requestReset} className="space-y-4">
            <p className="text-sm text-[var(--brand-muted)]">
              Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.
            </p>
            <div>
              <label className="text-sm font-medium">E-Mail</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                placeholder="deine@email.de"
              />
            </div>
            {err && <div className="text-sm rounded p-2" style={{ background: "#FEF2F2", color: "#991B1B" }}>{err}</div>}
            {msg && <div className="text-sm rounded p-2" style={{ background: "#F0FDF4", color: "#166534" }}>{msg}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
              {loading ? "Bitte warten…" : "Link senden"}
            </Button>
            <a href="/app" className="block text-center text-xs text-[var(--brand-muted)] hover:text-[var(--brand-ink)] hover:underline mt-2">
              ← Zurück zur Anmeldung
            </a>
          </form>
        )}

        {step === "reset" && (
          <form onSubmit={resetPassword} className="space-y-4">
            <p className="text-sm text-[var(--brand-muted)]">
              Lege jetzt dein neues Passwort fest.
            </p>
            <div>
              <label className="text-sm font-medium">Neues Passwort</label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="min. 8 Zeichen"
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--brand-muted)] hover:text-[var(--brand-ink)]"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Passwort bestätigen</label>
              <Input
                type={showPw ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                required
              />
            </div>
            {err && <div className="text-sm rounded p-2" style={{ background: "#FEF2F2", color: "#991B1B" }}>{err}</div>}
            {msg && <div className="text-sm rounded p-2" style={{ background: "#F0FDF4", color: "#166534" }}>{msg}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
              {loading ? "Bitte warten…" : "Passwort speichern"}
            </Button>
          </form>
        )}

        {step === "done" && (
          <div className="space-y-4 text-center">
            <div className="text-sm rounded p-3" style={{ background: "#F0FDF4", color: "#166534" }}>
              {msg || "Passwort erfolgreich geändert!"}
            </div>
            <a href="/app" className="inline-block mt-2 text-sm text-[var(--brand-primary)] hover:underline font-medium">
              Jetzt anmelden →
            </a>
          </div>
        )}
      </Card>
    </div>
  );
};
